import React from 'react';
import { partitionComponents, isComponent, useTheme, ThemeProvider } from '@redsift/design-system';
import { DialogContent } from '../dialog-content';
import { DialogTrigger } from '../dialog-trigger';

import { DialogContext } from './context';
import { DialogProps, DialogSize } from './types';
import { useDialog } from './useDialog';

const COMPONENT_NAME = 'Dialog';
const CLASSNAME = 'redsift-dialog';

/**
 * Dialog displays modal content that requires user attention or interaction.
 * Built on Floating UI for positioning and focus management.
 *
 * Composed of `Dialog.Trigger` and `Dialog.Content` sub-components.
 * Content can use `DialogContent.Header`, `DialogContent.Body`, and
 * `DialogContent.Actions` for structured layouts.
 *
 * Sizes: `small`, `medium`, `large`, `xlarge`, `fullscreen`
 *
 * @example
 * // Basic dialog
 * <Dialog>
 *   <Dialog.Trigger>
 *     <Button>Open Dialog</Button>
 *   </Dialog.Trigger>
 *   <Dialog.Content>
 *     <Dialog.Content.Header header="Confirmation" />
 *     <Dialog.Content.Body>Are you sure you want to continue?</Dialog.Content.Body>
 *     <Dialog.Content.Actions>
 *       <Button variant="secondary">Cancel</Button>
 *       <Button>Confirm</Button>
 *     </Dialog.Content.Actions>
 *   </Dialog.Content>
 * </Dialog>
 *
 * @example
 * // Controlled dialog with initialFocus on a specific element
 * const saveButtonRef = useRef(null);
 * <Dialog isOpen={isOpen} onOpen={setIsOpen} initialFocus={saveButtonRef}>
 *   <Dialog.Trigger><Button>Edit</Button></Dialog.Trigger>
 *   <Dialog.Content>
 *     <Dialog.Content.Header header="Edit Item" />
 *     <Dialog.Content.Body><TextField label="Name" /></Dialog.Content.Body>
 *     <Dialog.Content.Actions>
 *       <Button variant="secondary">Cancel</Button>
 *       <Button ref={saveButtonRef}>Save</Button>
 *     </Dialog.Content.Actions>
 *   </Dialog.Content>
 * </Dialog>
 *
 * @example
 * // Initial focus on header, body, or actions section
 * <Dialog initialFocus="header">...</Dialog>
 * <Dialog initialFocus="body">...</Dialog>
 * <Dialog initialFocus="actions">...</Dialog>
 *
 * @example
 * // Large dialog without close button
 * <Dialog size="large" hasCloseButton={false}>
 *   {...}
 * </Dialog>
 */
export const BaseDialog: React.FC<DialogProps> & {
  displayName?: string;
  className?: string;
} = (props) => {
  const {
    color,
    children,
    defaultOpen,
    hasCloseButton = true,
    initialFocus,
    isOpen,
    onOpen,
    size = DialogSize.medium,
    theme: propsTheme,
    triggerClassName,
  } = props;

  const theme = useTheme(propsTheme);

  const dialog = useDialog({
    color,
    defaultOpen,
    hasCloseButton,
    initialFocus,
    isOpen,
    onOpen,
    size,
    triggerClassName,
  });

  const [[trigger], [content]] = partitionComponents(React.Children.toArray(children), [
    isComponent('DialogTrigger'),
    isComponent('DialogContent'),
  ]);

  return (
    <ThemeProvider value={{ theme }}>
      <DialogContext.Provider value={dialog}>
        {trigger && isComponent('DialogTrigger')(trigger) ? trigger : null}
        {content && isComponent('DialogContent')(content) ? content : null}
      </DialogContext.Provider>
    </ThemeProvider>
  );
};
BaseDialog.className = CLASSNAME;
BaseDialog.displayName = COMPONENT_NAME;

export const Dialog = Object.assign(BaseDialog, {
  Trigger: DialogTrigger,
  Content: DialogContent,
});
